/*
 * Cookie Consent — vanilla show/hide + Shopify Customer Privacy API
 * integration for [data-cookie-consent]. Same cookie + initAll pattern
 * as assets/newsletter-popup.js and assets/announcement-bar.js.
 *
 *   Show    On load, if the `cookie-consent` cookie is unset, add the
 *           `.active` class (CSS handles the fade / slide in). In the
 *           theme editor (Shopify.designMode) it always shows so the
 *           merchant can style it.
 *
 *   Accept  Writes `cookie-consent=accepted` for `data-expiry-days`
 *           and calls window.Shopify.customerPrivacy.setTrackingConsent
 *           with analytics / marketing / preferences / sale_of_data
 *           all true. Hides the card.
 *
 *   Deny    Same, `denied` + all flags false.
 *
 *   Close   Treated identically to Deny — no choice = no tracking.
 *
 * Loaded from sections/cookie-consent.liquid via a `defer` script tag;
 * binds every [data-cookie-consent] on load and on shopify:section:load.
 */

const SELECTOR = '[data-cookie-consent]';
const INSTANCE_KEY = '_cookieConsentInstance';
const COOKIE_NAME = 'cookie-consent';

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

/*
 * Shopify Customer Privacy API. Available as window.Shopify.customerPrivacy
 * once Shopify's storefront runtime is ready. We defer the call so we don't
 * race the runtime; if the API never lands (older stores, storefronts with
 * consent disabled) we silently no-op — the cookie we set separately still
 * suppresses the banner on future loads.
 */
function setTrackingConsent(allow) {
  const flags = {
    analytics: allow,
    marketing: allow,
    preferences: allow,
    sale_of_data: allow,
  };
  const apply = () => {
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(flags, () => {});
    } catch (_) {
      /* silent — see block comment */
    }
  };
  if (window.Shopify && window.Shopify.customerPrivacy) {
    apply();
  } else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (err) => {
        if (!err && window.Shopify.customerPrivacy) apply();
      }
    );
  }
}

class CookieConsent {
  constructor(root) {
    this.root = root;
    this.acceptBtn = root.querySelector('[data-cookie-consent-accept]');
    this.denyBtn = root.querySelector('[data-cookie-consent-deny]');
    this.closeBtn = root.querySelector('[data-cookie-consent-close]');
    // Overlay (optional) is a sibling of the card, scoped within the
    // section element so we don't snag overlays from another banner.
    this.overlay = root.closest('.shopify-section')?.querySelector('[data-cookie-consent-overlay]') || null;
    this.delay = (parseFloat(root.dataset.delay) || 0) * 1000;
    this.expiryDays = parseFloat(root.dataset.expiryDays) || 365;
    this.showTimer = null;
    this.init();
  }

  init() {
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    const cookie = readCookie(COOKIE_NAME);

    if (inEditor) {
      // Always show in the customizer so the merchant can style it.
      this.show();
    } else if (!cookie) {
      this.showTimer = setTimeout(() => this.show(), this.delay);
    }

    if (this.acceptBtn) {
      this.acceptBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.accept();
      });
    }
    if (this.denyBtn) {
      this.denyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.deny();
      });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.deny();
      });
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.deny();
      });
    }

    this.root[INSTANCE_KEY] = this;
  }

  show() {
    this.root.classList.add('active');
  }

  hide() {
    this.root.classList.remove('active');
  }

  accept() {
    writeCookie(COOKIE_NAME, 'accepted', this.expiryDays);
    setTrackingConsent(true);
    this.hide();
  }

  deny() {
    writeCookie(COOKIE_NAME, 'denied', this.expiryDays);
    setTrackingConsent(false);
    this.hide();
  }

  destroy() {
    if (this.showTimer) clearTimeout(this.showTimer);
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new CookieConsent(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
